import { Box, Skeleton, Typography } from "@mui/material";

import { styled } from "@mui/material/styles";
import { CalendarIcon, HistoryIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import { TabButton } from "components/TabButton/TabButton";
import { ISubject } from "interfaces/subject.interface";

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: "solid",
  borderColor: theme.vars.palette.backgrounds.neutral.strong.default,
  paddingInline: theme.spacing(6),
  [theme.breakpoints.down("md")]: {
    paddingInline: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    paddingInline: theme.spacing(1.5),
    flexDirection: "column",
  },
}));

const TabWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  paddingTop: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(1.5),
    "& > *": {
      flexGrow: 1,
    },
  },
}));

const TitleWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBlock: theme.spacing(1.5),
  height: 44,
  [theme.breakpoints.down("sm")]: {
    height: "auto",
    margin: 0,
    paddingTop: theme.spacing(1.5),
  },
}));

type TabsMenuProps = {
  subjectId: string;
  subject: ISubject | null;
  showSleepHistory: boolean;
};

export function TabsMenu({
  subjectId,
  subject,
  showSleepHistory,
}: TabsMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // When going to history from session, we remember the sessionId in state for when we go back
  const locationSessionId: string | undefined = location.state?.sessionId;
  const sessionId = searchParams.get("sessionId");

  const sex = {
    male: t("Male"),
    female: t("Female"),
  };

  return (
    <Header>
      <TitleWrapper>
        {subject ? (
          <>
            <Typography variant={"headingSmallSemibold"}>
              {subject.devices?.data?.[0]?.name ?? "-"}
            </Typography>
            <Typography>{subject.identifier}</Typography>
            <Typography>{`${subject.sex ? sex[subject.sex] : ""} ${subject.birth_year}`}</Typography>
          </>
        ) : (
          <Skeleton width={400} height={40} />
        )}
      </TitleWrapper>
      <TabWrapper displayPrint={"none"}>
        <TabButton
          startIcon={<CalendarIcon />}
          component={Link}
          to={{
            pathname: `/subject/${subjectId}/sessions`,
            search: locationSessionId
              ? new URLSearchParams([
                  ["sessionId", locationSessionId],
                ]).toString()
              : undefined,
          }}
          aria-current={location.pathname.endsWith("/sessions")}
        >
          {t("Sessions")}
        </TabButton>
        {showSleepHistory && (
          <TabButton
            startIcon={<HistoryIcon />}
            component={Link}
            to={`/subject/${subjectId}/history`}
            state={{ sessionId }}
            aria-current={location.pathname.endsWith("/history")}
          >
            {t("History")}
          </TabButton>
        )}
      </TabWrapper>
    </Header>
  );
}
